<template>
    <el-table 
        :data="huonekortit" 
        @sort-change="sortChanged" 
        :default-sort="{ prop: 'tilanimike', order: 'ascending' }"
        ref="tableRef" 
        :scrollbar-always-on="true"
        :expand-row-keys="expandedRows"
        @expand-change="handleRowExpand"
        row-key="id">
        <el-table-column type="expand">
            <template #default="{ row }">
                <ComHuonekorttiExpandedRow
                    :row="row"
                    :liitteet="liitteet"
                    :liitteet-loading="liitteetLoading"
                    @download-liite="downloadLiiteClick" />
            </template>
        </el-table-column>
        <el-table-column label="Tilanimike" width="180" sortable="custom" prop="tilanimike">
            <template #default="scope">
                <template v-if="edit === scope.row.id">
                    <el-select size="small" v-model="editedHuonekortti.tilanimike.id">
                        <el-option
                            v-for="tilanimike in tilanimikkeet"
                            :key="tilanimike.id"
                            :label="tilanimike.nimi"
                            :value="tilanimike.id"></el-option>
                    </el-select>
                </template>
                <template v-else>
                    <div class="tilanimike-cell">
                        <router-link
                            :to="{ name: 'Huonekortti', params: { huonekorttiId: scope.row.id } }"
                            v-slot="{ href, navigate }"
                            custom>
                            <el-link type="primary" :href="href" @click="navigate">{{ scope.row.tilanimike.nimi }}</el-link>
                        </router-link>
                        <el-space :size="4">
                            <el-icon v-if="isDuplicateHuonekortti(scope.row)" color="#e6a23c">
                                <Warning />
                            </el-icon>
                            <el-icon v-show="getLiitteetCount(scope.row.id)">
                                <Paperclip></Paperclip>
                            </el-icon>
                        </el-space>
                    </div>
                </template>
            </template>
        </el-table-column>
        <el-table-column min-width="250px" label="Täsmennys" show-overflow-tooltip sortable="custom" prop="tasmennys">
            <template #default="scope">
                <template v-if="edit === scope.row.id">
                    <el-input size="small" v-model="editedHuonekortti.toiminta"></el-input>
                </template>
                <template v-else>
                    {{ scope.row.toiminta }}
                </template>
            </template>
        </el-table-column>
        <el-table-column label="Status" width="120" sortable="custom" prop="status">
            <template #default="scope">
                <ComStatus :huonekortti="scope.row" v-model="scope.row.status"></ComStatus>
            </template>
        </el-table-column>
        <el-table-column show-overflow-tooltip label="Vastuuyksikkö" sortable="custom" prop="vastuuyksikko" width="180">
            <template #default="scope">
                <template v-if="edit === scope.row.id">
                    <el-select v-if="writableVastuuyksikot" size="small" v-model="editedHuonekortti.vastuuyksikko.id">
                        <el-option
                            v-for="vastuuyksikko in writableVastuuyksikot"
                            :key="vastuuyksikko.id"
                            :label="vastuuyksikko.nimi"
                            :value="vastuuyksikko.id"></el-option>
                    </el-select>
                </template>
                <template v-else>
                    {{ scope.row.vastuuyksikko.nimi }}
                </template>
            </template>
        </el-table-column>
        <el-table-column
            prop="tilat"
            label="Tilat"
            min-width="300"
            >
            <template #default="scope">
                <template v-if="edit === scope.row.id">
                    <el-row :gutter="8">
                        <el-col :span="10">
                            <label>Tilatunnus</label>
                        </el-col>
                        <el-col :span="7">
                            <label>Pinta-ala</label>
                        </el-col>
                        <el-col :span="7">
                            <label></label>
                        </el-col>
                    </el-row>
                    <template v-for="(tila, index) in editedHuonekortti.tilat" :key="tila.id">
                        <el-row :gutter="8" class="tilat-row">
                            <el-col :span="10">
                                <el-input
                                    v-model="tila.tilatunnus"
                                    size="small"
                                    :status="validationErrors.has(getValidationKey(tila, index)) ? 'error' : ''"
                                    @blur="validateTilatunnusInput(tila, tila.tilatunnus, index)"
                                    @input="validationErrors.delete(getValidationKey(tila, index))"
                                    placeholder="Tilatunnus">
                                </el-input>
                            </el-col>
                            <el-col :span="7">
                                <el-input
                                    v-model="tila.pintaAla"
                                    size="small"
                                    type="number"
                                    placeholder="m2"></el-input>
                            </el-col>
                            <el-col :span="7">
                                <ComMinusButton @click="removeTila(editedHuonekortti, index)" />
                                <ComPlusButton
                                    v-if="index + 1 === editedHuonekortti.tilat?.length"
                                    @click="addTila(editedHuonekortti)" />
                            </el-col>
                        </el-row>
                        <el-row v-if="validationErrors.has(getValidationKey(tila, index))" class="tilat-row">
                            <el-col :span="24" class="validation-error" style="text-align: left">
                                {{ validationErrors.get(getValidationKey(tila, index)) }}
                            </el-col>
                        </el-row>
                    </template>
                </template>
                <el-tooltip v-else :disabled="edit !== undefined" placement="top">
                    <template #content>
                        <div v-for="tila in scope.row.tilat" :key="tila.id">
                            {{ tila.tilatunnus }} {{ tila.pintaAla ? `(${tila.pintaAla} m²)` : '' }}
                        </div>
                    </template>
                    <div class="tilat-column">
                        <div class="tilat-display gap-2">
                            <el-space :size="1">
                                <template v-for="tila in scope.row.tilat" :key="tila.id">
                                    <el-tag size="small" :type="isDuplicateTilatunnus(tila.tilatunnus) ? 'warning' : 'info'">{{ tila.tilatunnus }}</el-tag>
                                </template>
                            </el-space>
                        </div>
                    </div>
                </el-tooltip>
            </template>
        </el-table-column>
        <el-table-column label="lkm" width="100">
            <template #default="scope">{{ scope.row.tilat.length }} kpl</template>
        </el-table-column>
        <el-table-column align="right" width="100" fixed="right">
            <template #default="scope">
                <template v-if="edit === scope.row.id">
                    <ComCancelIconButton @click="cancelEdit()"></ComCancelIconButton>
                    <ComSaveButton @click="saveEditWithValidation(scope.row)"></ComSaveButton>
                </template>
                <el-badge 
                    v-if="!edit && kommentitCount" 
                    is-dot 
                    :hidden="!getKommentitCount(scope.row.id)" 
                    class="badge">
                    <ComCommentButton @click="showHuonekorttiKommentit(scope.row.id)"></ComCommentButton>
                </el-badge>
                <template v-if="!edit && writableVastuuyksikot?.some((v) => v.id === scope.row.vastuuyksikko.id)">
                    <el-dropdown trigger="click">
                        <ComMoreButton/>
                        <template #dropdown>
                            <el-dropdown-item @click="editHuonekortti(scope.row)">Muokkaa</el-dropdown-item>
                            <el-dropdown-item v-if="checkPoistoOikeus(scope.row)" @click="deleteHuonekorttiClick(scope.row)">
                                Poista
                            </el-dropdown-item>
                            <el-dropdown-item v-if="paakayttaja" @click="addMallihuonekorttiClick(scope.row)">
                                Luo mallihuonekortti
                            </el-dropdown-item>
                        </template>
                    </el-dropdown>
                </template>
            </template>
        </el-table-column>
    </el-table>
    <ComMallihuonekorttiEditToimintaDialog
        v-if="mallihuonekortti"
        v-model:dialog="mallihuonekorttiDialog"
        :huonekortti="mallihuonekortti" />
    <el-drawer
        v-model="showDrawer"
        size="50%"
        title="Kommentointi"
        destroy-on-close
        @close="viite = undefined"> 
        <ComKommentointi v-if="viite" :paakayttaja="paakayttaja" :viite="viite" :tyyppi="1"></ComKommentointi>
    </el-drawer>
</template>

<script setup lang="ts">
import ComCancelIconButton from "@/components/common/buttons/ComCancelIconButton.vue"
import ComMinusButton from "@/components/common/buttons/ComMinusButton.vue"
import ComPlusButton from "@/components/common/buttons/ComPlusButton.vue"
import ComSaveButton from "@/components/common/buttons/ComSaveButton.vue"
import ComKommentointi from "@/components/kommentointi/ComKommentointi.vue"
import ComStatus from "../ComStatus.vue"
import { Paperclip, Warning } from "@element-plus/icons-vue"
import ComMoreButton from "@/components/common/buttons/ComMoreButton.vue"
import ComMallihuonekorttiEditToimintaDialog from "@/components/hanke/huonekortit/table/ComMallihuonekorttiEditToimintaDialog.vue"
import { huonekorttiTableEditable } from "./huonekorttiTableEditable"
import { ref } from "vue"
import ComCommentButton from "@/components/common/buttons/ComCommentButton.vue"
import ComHuonekorttiExpandedRow from "./ComHuonekorttiExpandedRow.vue"
import { useTilatunnusValidation } from "@/methods/tilatunnusValidationComposables"
import { useHuonekorttiTableWithDuplicates, useHuonekorttiExpansion } from "./huonekorttiTable"
import { hankePage } from "@/views/hanke"
import { TilaResponse } from "@/api/models/tila-response"
import { HuonekorttiResponse } from "@/api/models/huonekortti-response"
import { HuonekorttiRequestPut } from "@/api/models/huonekortti-request-put"

const viite = ref()
const showDrawer = ref(false)
const {
    huonekortit,
    mallihuonekortti,
    mallihuonekorttiDialog,
    tilanimikkeet,
    edit,
    editedHuonekortti,
    paakayttaja,
    writableVastuuyksikot,
    tableRef,
    kommentitCount,
    sortChanged,
    removeTila,
    addTila,
    cancelEdit,
    saveEdit,
    editHuonekortti,
    addMallihuonekorttiClick,
    checkPoistoOikeus,
    deleteHuonekorttiClick,
    getKommentitCount,
    getLiitteetCount
} = huonekorttiTableEditable()

const { id: hankeId } = hankePage()
const { validateTilatunnus, showDuplicateConfirmation } = useTilatunnusValidation()
const { isDuplicateTilatunnus, isDuplicateHuonekortti } = useHuonekorttiTableWithDuplicates(hankeId)
const { expandedRows, liitteet, liitteetLoading, handleRowExpand, downloadLiiteClick } = useHuonekorttiExpansion()
const validationErrors = ref<Map<string | number, string>>(new Map())

const getValidationKey = (tila: TilaResponse, index: number) => tila.id || tila.tilatunnus || `index-${index}`

const validateTilatunnusInput = async (tila: TilaResponse, tilatunnus: string | undefined, index: number) => {
    const key = getValidationKey(tila, index)
    
    if (!tilatunnus?.trim() || !hankeId.value) {
        validationErrors.value.delete(key)
        return
    }

    // Skip validation if tilatunnus hasn't changed
    const originalHuonekortti = huonekortit.value?.find(h => h.id === edit.value)
    const originalTila = originalHuonekortti?.tilat?.find(t => t.id === tila.id)
    if (originalTila?.tilatunnus === tilatunnus.trim()) {
        validationErrors.value.delete(key)
        return
    }

    try {
        const isAvailable = await validateTilatunnus(hankeId.value, tilatunnus)
        if (isAvailable) {
            validationErrors.value.delete(key)
        } else {
            validationErrors.value.set(key, "Tilatunnus on jo käytössä")
        }
    } catch (error) {
        validationErrors.value.set(key, "Validointi epäonnistui")
    }
}

const validateAllTilatunnusBeforeSave = async (huonekortti: HuonekorttiRequestPut) => {
    // Get original huonekortti to compare tilatunnus changes
    const originalHuonekortti = huonekortit.value?.find(h => h.id === edit.value)

    const validationPromises = huonekortti.tilat?.map(async (tila: TilaResponse, index: number) => {
        if (tila.tilatunnus?.trim()) {
            // Check if this tilatunnus has changed from the original
            const originalTila = originalHuonekortti?.tilat?.find(t => t.id === tila.id)
            const hasChanged = !originalTila || originalTila.tilatunnus !== tila.tilatunnus?.trim()

            // Only validate if tilatunnus has changed
            if (hasChanged) {
                await validateTilatunnusInput(tila, tila.tilatunnus, index)
            }
        }
    }) || []

    await Promise.all(validationPromises)
    return validationErrors.value.size === 0
}

const saveEditWithValidation = async (item: HuonekorttiResponse) => {
    const isValid = await validateAllTilatunnusBeforeSave(editedHuonekortti.value)
    
    if (!isValid) {
        const duplicates: string[] = []
        validationErrors.value.forEach((message, key) => {
            if (message === "Tilatunnus on jo käytössä") {
                const tila = editedHuonekortti.value.tilat?.find((t, index) => getValidationKey(t, index) === key)
                if (tila?.tilatunnus) {
                    duplicates.push(tila.tilatunnus)
                }
            }
        })
        
        const shouldContinue = await showDuplicateConfirmation(duplicates)
        if (shouldContinue) {
            saveEdit(item)
            validationErrors.value.clear()
        }
    } else {
        saveEdit(item)
        validationErrors.value.clear()
    }
}

const showHuonekorttiKommentit = (id : number) => {
    viite.value = id
    showDrawer.value = true
}

</script>

<style scoped>
.badge {
    margin-right: 12px;
}
.badge :deep(sup) {
    top: 4px;
}
:deep(.el-scrollbar__wrap) {
    overflow: auto !important;
}

.validation-error {
    color: #f56565;
    font-size: 12px;
    margin-top: 4px;
    line-height: 1.2;
}

.tilat-row {
    padding-top: 3px;
    padding-bottom: 3px;
}

.tilat-column {
    display: flex;
    align-items: center;
    gap: 8px;
}

.tilat-display {
    line-height: 1.4;
}


.tilanimike-cell {
    display: flex;
    align-items: center;
}
</style>
